import { FieldType, REQUIRED_RICH_TEXT_NODES, RichTextNodeType, } from '@dossierhq/core';
import isEqual from 'lodash/isEqual.js';
import { assertIsDefined } from '../../utils/AssertUtils.js';
export const RichTextNodePlaceholders = [
    REQUIRED_RICH_TEXT_NODES,
    [RichTextNodeType.code, RichTextNodeType['code-highlight']],
    [RichTextNodeType.list, RichTextNodeType.listitem],
].map((nodes) => ({ name: nodes.join(', '), nodes }));
const RichTextNodesInPlaceholders = new Set(RichTextNodePlaceholders.flatMap((placeholder) => placeholder.nodes));
export const REQUIRED_NODES_PLACEHOLDER = RichTextNodePlaceholders[0];
export function initializeSchemaEditorState() {
    return {
        status: 'uninitialized',
        schema: null,
        schemaWillBeUpdatedDueToSave: false,
        entityTypes: [],
        deletedEntityTypes: [],
        componentTypes: [],
        deletedComponentTypes: [],
        indexes: [],
        deletedIndexes: [],
        patterns: [],
        activeSelector: null,
        activeSelectorMenuScrollSignal: 0,
        activeSelectorEditorScrollSignal: 0,
    };
}
export function reduceSchemaEditorState(state, action) {
    const newState = action.reduce(state);
    // if (state !== newState) console.log(`State changed for ${action.constructor.name}`, state, action, newState);
    return newState;
}
// STATUS RESOLVERS
function resolveSchemaStatus(state) {
    if (state.status === 'uninitialized') {
        return state.status;
    }
    for (const type of [
        ...state.entityTypes,
        ...state.componentTypes,
        ...state.indexes,
        ...state.patterns,
    ]) {
        if (type.status !== '') {
            return 'changed';
        }
    }
    if (state.deletedEntityTypes.length > 0 || state.deletedComponentTypes.length > 0) {
        return 'changed';
    }
    return '';
}
function withResolvedSchemaStatus(state) {
    const newStatus = resolveSchemaStatus(state);
    if (newStatus === state.status)
        return state;
    return { ...state, status: newStatus };
}
function resolveTypeStatus(state) {
    if (state.status === 'new')
        return state.status;
    if (state.name !== state.existingName)
        return 'changed';
    if (state.kind === 'entity') {
        if (state.publishable !== state.existingPublishable)
            return 'changed';
        if (state.nameField !== state.existingNameField)
            return 'changed';
        if (state.authKeyPattern !== state.existingAuthKeyPattern)
            return 'changed';
    }
    else {
        if (state.adminOnly !== state.existingAdminOnly)
            return 'changed';
    }
    if (state.deletedFields.length > 0)
        return 'changed';
    for (const field of state.fields) {
        if (field.status !== '')
            return 'changed';
    }
    for (let i = 0; i < state.existingFieldOrder.length; i++) {
        if (state.existingFieldOrder[i] !== state.fields[i].name)
            return 'changed';
    }
    return '';
}
function withResolvedTypeStatus(state) {
    const newStatus = resolveTypeStatus(state);
    if (newStatus === state.status)
        return state;
    return { ...state, status: newStatus };
}
function resolveFieldStatus(state) {
    const { existingFieldSpec } = state;
    if (existingFieldSpec === null) {
        return 'new';
    }
    if (existingFieldSpec.name !== state.name) {
        return 'changed';
    }
    if (existingFieldSpec.required !== state.required) {
        return 'changed';
    }
    if (existingFieldSpec.adminOnly !== state.adminOnly) {
        return 'changed';
    }
    if ((existingFieldSpec.type === FieldType.Reference ||
        existingFieldSpec.type === FieldType.RichText) &&
        !isEqual(state.entityTypes, existingFieldSpec.entityTypes)) {
        return 'changed';
    }
    if ((existingFieldSpec.type === FieldType.Component ||
        existingFieldSpec.type === FieldType.RichText) &&
        !isEqual(state.componentTypes, existingFieldSpec.componentTypes)) {
        return 'changed';
    }
    if (existingFieldSpec.type === FieldType.Number &&
        existingFieldSpec.integer !== !!state.integer) {
        return 'changed';
    }
    if (existingFieldSpec.type === FieldType.RichText) {
        if (!isEqual(state.linkEntityTypes, existingFieldSpec.linkEntityTypes)) {
            return 'changed';
        }
        if (!isEqual(state.richTextNodesWithPlaceholders, state.existingRichTextNodesWithPlaceholders)) {
            return 'changed';
        }
    }
    if (existingFieldSpec.type === FieldType.String) {
        if (state.index !== existingFieldSpec.index) {
            return 'changed';
        }
        if (state.matchPattern !== existingFieldSpec.matchPattern) {
            return 'changed';
        }
        if (!!state.multiline !== !!existingFieldSpec.multiline) {
            return 'changed';
        }
        if (!isEqual(state.values, existingFieldSpec.values)) {
            return 'changed';
        }
    }
    return '';
}
function withResolvedFieldStatus(state) {
    const newStatus = resolveFieldStatus(state);
    if (newStatus === state.status)
        return state;
    return { ...state, status: newStatus };
}
function resolveIndexStatus(state) {
    if (!state.existingIndexSpec) {
        return 'new';
    }
    if (state.name !== state.existingIndexSpec.name) {
        return 'changed';
    }
    if (state.type !== state.existingIndexSpec.type) {
        return 'changed';
    }
    return '';
}
function withResolvedIndexStatus(state) {
    const newStatus = resolveIndexStatus(state);
    if (newStatus === state.status)
        return state;
    return { ...state, status: newStatus };
}
function resolvePatternStatus(state) {
    if (!state.existingPatternSpec) {
        return 'new';
    }
    if (state.name !== state.existingPatternSpec.name) {
        return 'changed';
    }
    if (state.pattern !== state.existingPatternSpec.pattern) {
        return 'changed';
    }
    return '';
}
function withResolvedPatternStatus(state) {
    const newStatus = resolvePatternStatus(state);
    if (newStatus === state.status)
        return state;
    return { ...state, status: newStatus };
}
// ACTION HELPERS
class TypeAction {
    kind;
    typeName;
    constructor({ kind, typeName }) {
        this.kind = kind;
        this.typeName = typeName;
    }
    reduce(state) {
        const typeCollection = this.kind === 'entity' ? state.entityTypes : state.componentTypes;
        const typeIndex = typeCollection.findIndex((it) => it.name === this.typeName);
        if (typeIndex < 0)
            throw new Error(`No such ${this.kind} type ${this.typeName}`);
        const currentTypeDraft = typeCollection[typeIndex];
        let newTypeDraft = this.reduceType(currentTypeDraft);
        if (newTypeDraft === currentTypeDraft) {
            return state;
        }
        newTypeDraft = { ...newTypeDraft, status: resolveTypeStatus(newTypeDraft) };
        const newTypeCollection = [...typeCollection];
        newTypeCollection[typeIndex] = newTypeDraft;
        const newState = { ...state };
        if (this.kind === 'entity') {
            newState.entityTypes = newTypeCollection;
        }
        else {
            newState.componentTypes = newTypeCollection;
        }
        newState.status = resolveSchemaStatus(newState);
        return newState;
    }
}
class FieldAction extends TypeAction {
    fieldName;
    constructor(fieldSelector) {
        super(fieldSelector);
        this.fieldName = fieldSelector.fieldName;
    }
    reduceType(typeDraft) {
        const fieldIndex = typeDraft.fields.findIndex((it) => it.name === this.fieldName);
        if (fieldIndex < 0)
            throw new Error(`No such field ${this.fieldName} in type ${this.typeName}`);
        const currentFieldDraft = typeDraft.fields[fieldIndex];
        const newFieldDraft = this.reduceField(currentFieldDraft);
        return replaceFieldWithIndex(typeDraft, fieldIndex, withResolvedFieldStatus(newFieldDraft));
    }
}
class PatternAction {
    name;
    constructor({ name }) {
        this.name = name;
    }
    reduce(state) {
        const patternIndex = state.patterns.findIndex((it) => it.name === this.name);
        if (patternIndex < 0)
            throw new Error(`No such pattern ${this.name}`);
        const currentDraft = state.patterns[patternIndex];
        const newDraft = this.reducePattern(currentDraft);
        if (newDraft === currentDraft) {
            return state;
        }
        const newPatterns = [...state.patterns];
        newPatterns[patternIndex] = newDraft;
        const newState = { ...state, patterns: newPatterns };
        newState.status = resolveSchemaStatus(newState);
        return newState;
    }
}
function reduceEntityTypes(state, reduceType) {
    let changedEntityTypes = false;
    const newEntityTypes = state.entityTypes.map((typeDraft) => {
        const newTypeDraft = reduceType(typeDraft);
        if (newTypeDraft !== typeDraft) {
            changedEntityTypes = true;
            return withResolvedTypeStatus(newTypeDraft);
        }
        return newTypeDraft;
    });
    if (!changedEntityTypes) {
        return state;
    }
    return withResolvedSchemaStatus({ ...state, entityTypes: newEntityTypes });
}
function reduceFieldsOfAllTypes(state, reduceField) {
    function reduceTypeFields(typeDraft) {
        let changedFields = false;
        const newFields = typeDraft.fields.map((fieldDraft) => {
            const newFieldDraft = reduceField(fieldDraft);
            if (newFieldDraft !== fieldDraft) {
                changedFields = true;
                return withResolvedFieldStatus(newFieldDraft);
            }
            return newFieldDraft;
        });
        if (changedFields) {
            const newTypeDraft = { ...typeDraft, fields: newFields };
            return withResolvedTypeStatus(newTypeDraft);
        }
        return typeDraft;
    }
    // Entity types
    let changedEntityTypes = false;
    const newEntityTypes = state.entityTypes.map((typeDraft) => {
        const newTypeDraft = reduceTypeFields(typeDraft);
        if (newTypeDraft !== typeDraft) {
            changedEntityTypes = true;
        }
        return newTypeDraft;
    });
    // Component types
    let changedComponentTypes = false;
    const newComponentTypes = state.componentTypes.map((typeDraft) => {
        const newTypeDraft = reduceTypeFields(typeDraft);
        if (newTypeDraft !== typeDraft) {
            changedComponentTypes = true;
        }
        return newTypeDraft;
    });
    if (!changedEntityTypes && !changedComponentTypes) {
        return state;
    }
    const newState = {
        ...state,
        entityTypes: changedEntityTypes ? newEntityTypes : state.entityTypes,
        componentTypes: changedComponentTypes ? newComponentTypes : state.componentTypes,
    };
    newState.status = resolveSchemaStatus(newState);
    return newState;
}
function replaceFieldWithIndex(typeDraft, fieldIndex, newFieldDraft) {
    if (typeDraft.fields[fieldIndex] === newFieldDraft) {
        return typeDraft;
    }
    const newFields = [...typeDraft.fields];
    newFields[fieldIndex] = newFieldDraft;
    const newTypeDraft = { ...typeDraft, fields: newFields };
    return newTypeDraft;
}
// ACTIONS
class AddTypeAction {
    kind;
    name;
    constructor(kind, name) {
        this.kind = kind;
        this.name = name;
    }
    reduce(state) {
        const typeDraft = {
            status: 'new',
            name: this.name,
            existingName: null,
            deletedFields: [],
            fields: [],
        };
        const newState = {
            ...state,
            activeSelector: { kind: this.kind, typeName: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        if (this.kind === 'entity') {
            newState.entityTypes = [
                ...newState.entityTypes,
                {
                    ...typeDraft,
                    kind: 'entity',
                    authKeyPattern: null,
                    existingAuthKeyPattern: null,
                    publishable: true,
                    existingPublishable: true,
                    nameField: null,
                    existingNameField: null,
                    existingFieldOrder: [],
                },
            ];
            newState.entityTypes.sort((a, b) => a.name.localeCompare(b.name));
        }
        else {
            newState.componentTypes = [
                ...newState.componentTypes,
                {
                    ...typeDraft,
                    kind: 'component',
                    adminOnly: false,
                    existingAdminOnly: false,
                    existingFieldOrder: [],
                },
            ];
            newState.componentTypes.sort((a, b) => a.name.localeCompare(b.name));
        }
        return withResolvedSchemaStatus(newState);
    }
}
class AddFieldAction extends TypeAction {
    fieldName;
    constructor(typeSelector, fieldName) {
        super(typeSelector);
        this.fieldName = fieldName;
    }
    reduceType(typeSpec) {
        const field = {
            name: this.fieldName,
            status: 'new',
            type: FieldType.String,
            multiline: false,
            list: false,
            required: false,
            adminOnly: false,
            existingFieldSpec: null,
        };
        const fields = [...typeSpec.fields, field];
        return { ...typeSpec, fields };
    }
}
class AddIndexAction {
    name;
    constructor(name) {
        this.name = name;
    }
    reduce(state) {
        const indexDraft = {
            status: 'new',
            name: this.name,
            type: 'unique',
            existingIndexSpec: null,
        };
        const newState = {
            ...state,
            indexes: [...state.indexes, indexDraft].sort((a, b) => a.name.localeCompare(b.name)),
            activeSelector: { kind: 'index', name: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        newState.status = resolveSchemaStatus(newState);
        return newState;
    }
}
class AddPatternAction {
    name;
    constructor(name) {
        this.name = name;
    }
    reduce(state) {
        const patternDraft = {
            status: 'new',
            name: this.name,
            pattern: '',
            existingPatternSpec: null,
        };
        const newState = {
            ...state,
            patterns: [...state.patterns, patternDraft].sort((a, b) => a.name.localeCompare(b.name)),
            activeSelector: { kind: 'pattern', name: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        newState.status = resolveSchemaStatus(newState);
        return newState;
    }
}
class ChangeFieldAdminOnlyAction extends FieldAction {
    adminOnly;
    constructor(fieldSelector, adminOnly) {
        super(fieldSelector);
        this.adminOnly = adminOnly;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.adminOnly === this.adminOnly) {
            return fieldDraft;
        }
        return { ...fieldDraft, adminOnly: this.adminOnly };
    }
}
class ChangeFieldAllowedEntityTypesAction extends FieldAction {
    entityTypes;
    constructor(fieldSelector, entityTypes) {
        super(fieldSelector);
        this.entityTypes = [...entityTypes].sort();
    }
    reduceField(fieldDraft) {
        if (isEqual(fieldDraft.entityTypes, this.entityTypes)) {
            return fieldDraft;
        }
        return { ...fieldDraft, entityTypes: this.entityTypes };
    }
}
class ChangeFieldAllowedLinkEntityTypesAction extends FieldAction {
    linkEntityTypes;
    constructor(fieldSelector, linkEntityTypes) {
        super(fieldSelector);
        this.linkEntityTypes = [...linkEntityTypes].sort();
    }
    reduceField(fieldDraft) {
        if (isEqual(fieldDraft.linkEntityTypes, this.linkEntityTypes)) {
            return fieldDraft;
        }
        return { ...fieldDraft, linkEntityTypes: this.linkEntityTypes };
    }
}
class ChangeFieldAllowedRichTextNodesAction extends FieldAction {
    richTextNodesWithPlaceholders;
    constructor(fieldSelector, richTextNodes) {
        super(fieldSelector);
        this.richTextNodesWithPlaceholders = richTextNodes;
    }
    reduceField(fieldDraft) {
        const value = [...this.richTextNodesWithPlaceholders];
        if (value.length > 0 && !value.includes(REQUIRED_NODES_PLACEHOLDER.name)) {
            value.push(REQUIRED_NODES_PLACEHOLDER.name);
        }
        sortRichTextNodesWithPlaceholders(value);
        if (isEqual(fieldDraft.richTextNodesWithPlaceholders, value)) {
            return fieldDraft;
        }
        return { ...fieldDraft, richTextNodesWithPlaceholders: value };
    }
}
class ChangeFieldAllowedComponentTypesAction extends FieldAction {
    componentTypes;
    constructor(fieldSelector, componentTypes) {
        super(fieldSelector);
        this.componentTypes = [...componentTypes].sort();
    }
    reduceField(fieldDraft) {
        if (isEqual(fieldDraft.componentTypes, this.componentTypes)) {
            return fieldDraft;
        }
        return { ...fieldDraft, componentTypes: this.componentTypes };
    }
}
class ChangeFieldIndexAction extends FieldAction {
    index;
    constructor(fieldSelector, index) {
        super(fieldSelector);
        this.index = index;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.index === this.index) {
            return fieldDraft;
        }
        return { ...fieldDraft, index: this.index };
    }
}
class ChangeFieldIntegerAction extends FieldAction {
    integer;
    constructor(fieldSelector, integer) {
        super(fieldSelector);
        this.integer = integer;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.integer === this.integer) {
            return fieldDraft;
        }
        return { ...fieldDraft, integer: this.integer };
    }
}
class ChangeFieldMatchPatternAction extends FieldAction {
    pattern;
    constructor(fieldSelector, pattern) {
        super(fieldSelector);
        this.pattern = pattern;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.matchPattern === this.pattern) {
            return fieldDraft;
        }
        return { ...fieldDraft, matchPattern: this.pattern };
    }
}
class ChangeFieldMultilineAction extends FieldAction {
    multiline;
    constructor(fieldSelector, multiline) {
        super(fieldSelector);
        this.multiline = multiline;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.multiline === this.multiline) {
            return fieldDraft;
        }
        return { ...fieldDraft, multiline: this.multiline };
    }
}
class ChangeFieldRequiredAction extends FieldAction {
    required;
    constructor(fieldSelector, required) {
        super(fieldSelector);
        this.required = required;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.required === this.required) {
            return fieldDraft;
        }
        return { ...fieldDraft, required: this.required };
    }
}
class ChangeFieldTypeAction extends FieldAction {
    fieldType;
    list;
    constructor(fieldSelector, fieldType, list) {
        super(fieldSelector);
        this.fieldType = fieldType;
        this.list = list;
    }
    reduceField(fieldDraft) {
        if (fieldDraft.type === this.fieldType && fieldDraft.list === this.list) {
            return fieldDraft;
        }
        const newFieldDraft = { ...fieldDraft, type: this.fieldType, list: this.list };
        if (this.fieldType === FieldType.Number) {
            newFieldDraft.integer = false;
        }
        else {
            delete newFieldDraft.integer;
        }
        if (this.fieldType === FieldType.String) {
            newFieldDraft.multiline = !!newFieldDraft.multiline;
            newFieldDraft.index = newFieldDraft.index ?? null;
            newFieldDraft.matchPattern = newFieldDraft.matchPattern ?? null;
            newFieldDraft.values = newFieldDraft.values ?? [];
        }
        else {
            delete newFieldDraft.multiline;
            delete newFieldDraft.index;
            delete newFieldDraft.matchPattern;
            delete newFieldDraft.values;
        }
        if (this.fieldType === FieldType.RichText) {
            newFieldDraft.richTextNodesWithPlaceholders = [];
        }
        else {
            delete newFieldDraft.richTextNodesWithPlaceholders;
        }
        //TODO handle rich text?
        if (this.fieldType === FieldType.Reference) {
            newFieldDraft.entityTypes = [];
        }
        else {
            delete newFieldDraft.entityTypes;
        }
        //TODO handle linkEntityTypes?
        if (this.fieldType === FieldType.Component) {
            newFieldDraft.componentTypes = [];
        }
        else {
            delete newFieldDraft.componentTypes;
        }
        return newFieldDraft;
    }
}
class ChangeFieldValuesAction extends FieldAction {
    values;
    constructor(fieldSelector, values) {
        super(fieldSelector);
        this.values = [...values].sort((a, b) => a.value.localeCompare(b.value));
        // remove duplicates iterating from the end
        for (let i = this.values.length - 1; i > 0; i--) {
            if (this.values[i].value === this.values[i - 1].value) {
                this.values.splice(i, 1);
            }
        }
    }
    reduceField(fieldDraft) {
        if (isEqual(fieldDraft.values, this.values)) {
            return fieldDraft;
        }
        return { ...fieldDraft, values: this.values };
    }
}
class ChangePatternPatternAction extends PatternAction {
    pattern;
    constructor(selector, pattern) {
        super(selector);
        this.pattern = pattern;
    }
    reducePattern(draft) {
        if (draft.pattern === this.pattern) {
            return draft;
        }
        return withResolvedPatternStatus({ ...draft, pattern: this.pattern });
    }
}
class ChangeTypeAdminOnlyOrPublishableAction extends TypeAction {
    value;
    constructor(typeSelector, value) {
        super(typeSelector);
        this.value = value;
    }
    reduceType(typeDraft) {
        if (typeDraft.kind === 'entity') {
            if (typeDraft.publishable === this.value) {
                return typeDraft;
            }
            return { ...typeDraft, publishable: this.value };
        }
        if (typeDraft.adminOnly === this.value) {
            return typeDraft;
        }
        return { ...typeDraft, adminOnly: this.value };
    }
}
class ChangeTypeAuthKeyPatternAction extends TypeAction {
    pattern;
    constructor(typeSelector, pattern) {
        super(typeSelector);
        this.pattern = pattern;
    }
    reduceType(typeDraft) {
        if (typeDraft.authKeyPattern === this.pattern) {
            return typeDraft;
        }
        return { ...typeDraft, authKeyPattern: this.pattern };
    }
}
class ChangeTypeNameFieldAction extends TypeAction {
    nameField;
    constructor(typeSelector, nameField) {
        super(typeSelector);
        this.nameField = nameField;
    }
    reduceType(typeDraft) {
        if (typeDraft.kind === 'component' || typeDraft.nameField === this.nameField) {
            return typeDraft;
        }
        return withResolvedTypeStatus({ ...typeDraft, nameField: this.nameField });
    }
}
class DeleteFieldAction extends TypeAction {
    fieldName;
    constructor({ fieldName, ...typeSelector }) {
        super(typeSelector);
        this.fieldName = fieldName;
    }
    reduceType(typeDraft) {
        const fieldIndex = typeDraft.fields.findIndex((fieldDraft) => fieldDraft.name === this.fieldName);
        if (fieldIndex === -1) {
            return typeDraft;
        }
        const fields = [...typeDraft.fields];
        const [fieldDraft] = fields.splice(fieldIndex, 1);
        let deletedFields = typeDraft.deletedFields;
        if (fieldDraft.existingFieldSpec) {
            deletedFields = [...deletedFields, this.fieldName].sort();
        }
        const existingFieldOrder = typeDraft.existingFieldOrder.filter((it) => it !== this.fieldName);
        const newTypeDraft = { ...typeDraft, fields, deletedFields, existingFieldOrder };
        if ('nameField' in newTypeDraft && newTypeDraft.nameField === this.fieldName) {
            newTypeDraft.nameField = null;
        }
        return newTypeDraft;
    }
    reduce(state) {
        const newState = super.reduce(state);
        const activeSelector = newState.activeSelector;
        if (activeSelector &&
            activeSelector.kind == this.kind &&
            activeSelector.typeName == this.typeName &&
            'fieldName' in activeSelector &&
            activeSelector.fieldName == this.fieldName) {
            return { ...newState, activeSelector: null };
        }
        return newState;
    }
}
class DeleteIndexAction {
    selector;
    constructor(selector) {
        this.selector = selector;
    }
    reduce(state) {
        const indexDraftIndex = state.indexes.findIndex((it) => it.name === this.selector.name);
        if (indexDraftIndex < 0) {
            return state;
        }
        const indexes = [...state.indexes];
        const [indexDraft] = indexes.splice(indexDraftIndex, 1);
        let newState = { ...state, indexes };
        if (isEqual(newState.activeSelector, this.selector)) {
            newState.activeSelector = null;
        }
        // Remove references to index in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (fieldDraft.index === this.selector.name) {
                return { ...fieldDraft, index: null };
            }
            return fieldDraft;
        });
        if (indexDraft.existingIndexSpec) {
            newState.deletedIndexes = [...newState.deletedIndexes, this.selector.name].sort();
        }
        return withResolvedSchemaStatus(newState);
    }
}
class DeletePatternAction {
    selector;
    constructor(selector) {
        this.selector = selector;
    }
    reduce(state) {
        let newState = {
            ...state,
            patterns: state.patterns.filter((it) => it.name !== this.selector.name),
        };
        if (isEqual(newState.activeSelector, this.selector)) {
            newState.activeSelector = null;
        }
        // Remove references to pattern in entity types
        newState = reduceEntityTypes(newState, (typeDraft) => {
            if (typeDraft.authKeyPattern === this.selector.name) {
                return { ...typeDraft, authKeyPattern: null };
            }
            return typeDraft;
        });
        // Remove references to pattern in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (fieldDraft.matchPattern === this.selector.name) {
                return { ...fieldDraft, matchPattern: null };
            }
            return fieldDraft;
        });
        return withResolvedSchemaStatus(newState);
    }
}
class DeleteTypeAction {
    selector;
    constructor(selector) {
        this.selector = selector;
    }
    reduce(state) {
        let { activeSelector, entityTypes, deletedEntityTypes, componentTypes, deletedComponentTypes } = state;
        const typeDrafts = this.selector.kind === 'entity' ? entityTypes : componentTypes;
        const typeIndex = typeDrafts.findIndex((it) => it.name === this.selector.typeName);
        if (typeIndex < 0)
            throw new Error(`No such ${this.selector.kind} type ${this.selector.typeName}`);
        const typeDraft = typeDrafts[typeIndex];
        const newTypeDrafts = [...typeDrafts];
        newTypeDrafts.splice(typeIndex, 1);
        if (this.selector.kind === 'entity') {
            entityTypes = newTypeDrafts;
            if (typeDraft.existingName) {
                deletedEntityTypes = [...deletedEntityTypes, typeDraft.existingName].sort();
            }
        }
        else {
            componentTypes = newTypeDrafts;
            if (typeDraft.existingName) {
                deletedComponentTypes = [...deletedComponentTypes, typeDraft.existingName].sort();
            }
        }
        if (isEqual(activeSelector, this.selector)) {
            activeSelector = null;
        }
        let newState = {
            ...state,
            activeSelector,
            entityTypes,
            deletedEntityTypes,
            componentTypes,
            deletedComponentTypes,
        };
        // Remove references to type in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (this.selector.kind === 'entity') {
                if (fieldDraft.entityTypes?.includes(this.selector.typeName) ||
                    fieldDraft.linkEntityTypes?.includes(this.selector.typeName)) {
                    const newFieldDraft = { ...fieldDraft };
                    if (newFieldDraft.entityTypes) {
                        newFieldDraft.entityTypes = newFieldDraft.entityTypes.filter((it) => it !== this.selector.typeName);
                    }
                    if (newFieldDraft.linkEntityTypes) {
                        newFieldDraft.linkEntityTypes = newFieldDraft.linkEntityTypes.filter((it) => it !== this.selector.typeName);
                    }
                    return newFieldDraft;
                }
            }
            else {
                if (fieldDraft.componentTypes?.includes(this.selector.typeName)) {
                    return {
                        ...fieldDraft,
                        componentTypes: fieldDraft.componentTypes.filter((it) => it !== this.selector.typeName),
                    };
                }
            }
            return fieldDraft;
        });
        return withResolvedSchemaStatus(newState);
    }
}
class RenameFieldAction extends FieldAction {
    newFieldName;
    constructor(fieldSelector, newFieldName) {
        super(fieldSelector);
        this.newFieldName = newFieldName;
    }
    reduceField(fieldDraft) {
        if (this.newFieldName === fieldDraft.name) {
            return fieldDraft;
        }
        return { ...fieldDraft, name: this.newFieldName };
    }
    reduceType(typeDraft) {
        const superTypeDraft = super.reduceType(typeDraft);
        if (superTypeDraft === typeDraft) {
            return typeDraft;
        }
        const existingFieldOrder = superTypeDraft.existingFieldOrder.map((it) => it === this.fieldName ? this.newFieldName : it);
        const newTypeDraft = { ...superTypeDraft, existingFieldOrder };
        if ('nameField' in newTypeDraft && newTypeDraft.nameField === this.fieldName) {
            newTypeDraft.nameField = this.newFieldName;
        }
        if ('existingNameField' in newTypeDraft && newTypeDraft.existingNameField === this.fieldName) {
            newTypeDraft.existingNameField = this.newFieldName;
        }
        return newTypeDraft;
    }
    reduce(state) {
        const newState = super.reduce(state);
        if (newState === state) {
            return state;
        }
        return {
            ...newState,
            activeSelector: { kind: this.kind, typeName: this.typeName, fieldName: this.newFieldName },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
    }
}
class RenameIndexAction {
    selector;
    name;
    constructor(selector, name) {
        this.selector = selector;
        this.name = name;
    }
    reduce(state) {
        // Rename index
        let newState = {
            ...state,
            indexes: state.indexes.map((it) => {
                if (it.name === this.selector.name) {
                    return withResolvedIndexStatus({ ...it, name: this.name });
                }
                return it;
            }),
            activeSelector: { kind: 'index', name: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        // Sort indexes
        newState.indexes.sort((a, b) => a.name.localeCompare(b.name));
        // Rename references to index in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (fieldDraft.index === this.selector.name) {
                return { ...fieldDraft, index: this.name };
            }
            return fieldDraft;
        });
        return newState;
    }
}
class RenamePatternAction {
    selector;
    name;
    constructor(selector, name) {
        this.selector = selector;
        this.name = name;
    }
    reduce(state) {
        // Rename pattern
        let newState = {
            ...state,
            patterns: state.patterns.map((it) => {
                if (it.name === this.selector.name) {
                    return withResolvedPatternStatus({ ...it, name: this.name });
                }
                return it;
            }),
            activeSelector: { kind: 'pattern', name: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        // Sort patterns
        newState.patterns.sort((a, b) => a.name.localeCompare(b.name));
        // Rename references to pattern in entity types
        newState = reduceEntityTypes(newState, (typeDraft) => {
            if (typeDraft.authKeyPattern === this.selector.name) {
                return { ...typeDraft, authKeyPattern: this.name };
            }
            return typeDraft;
        });
        // Rename references to pattern in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (fieldDraft.matchPattern === this.selector.name) {
                return { ...fieldDraft, matchPattern: this.name };
            }
            return fieldDraft;
        });
        return newState;
    }
}
class RenameTypeAction extends TypeAction {
    name;
    constructor(selector, name) {
        super(selector);
        this.name = name;
    }
    reduceType(typeDraft) {
        return { ...typeDraft, name: this.name };
    }
    reduce(state) {
        const superState = super.reduce(state);
        // Rename type
        let newState = {
            ...superState,
            activeSelector: { kind: this.kind, typeName: this.name },
            activeSelectorMenuScrollSignal: state.activeSelectorMenuScrollSignal + 1,
            activeSelectorEditorScrollSignal: state.activeSelectorEditorScrollSignal + 1,
        };
        // Sort types
        if (this.kind === 'entity') {
            newState.entityTypes.sort((a, b) => a.name.localeCompare(b.name));
        }
        else {
            newState.componentTypes.sort((a, b) => a.name.localeCompare(b.name));
        }
        // Rename references to type in fields
        newState = reduceFieldsOfAllTypes(newState, (fieldDraft) => {
            if (this.kind === 'entity') {
                if (fieldDraft.entityTypes?.includes(this.typeName) ||
                    fieldDraft.linkEntityTypes?.includes(this.typeName)) {
                    return {
                        ...fieldDraft,
                        entityTypes: fieldDraft.entityTypes?.map((it) => it === this.typeName ? this.name : it),
                        linkEntityTypes: fieldDraft.linkEntityTypes?.map((it) => it === this.typeName ? this.name : it),
                    };
                }
            }
            else {
                if (fieldDraft.componentTypes?.includes(this.typeName)) {
                    return {
                        ...fieldDraft,
                        componentTypes: fieldDraft.componentTypes.map((it) => it === this.typeName ? this.name : it),
                    };
                }
            }
            return fieldDraft;
        });
        return newState;
    }
}
class ReorderFieldsAction extends TypeAction {
    fieldToMove;
    position;
    targetField;
    constructor(selector, fieldToMove, position, targetField) {
        super(selector);
        this.fieldToMove = fieldToMove;
        this.position = position;
        this.targetField = targetField;
    }
    reduceType(typeDraft) {
        if (this.fieldToMove === this.targetField) {
            return typeDraft;
        }
        const fields = [...typeDraft.fields];
        const fieldToMoveIndex = fields.findIndex((it) => it.name === this.fieldToMove);
        const [fieldToMove] = fields.splice(fieldToMoveIndex, 1);
        const targetFieldIndex = fields.findIndex((it) => it.name === this.targetField);
        if (this.position === 'after') {
            fields.splice(targetFieldIndex + 1, 0, fieldToMove);
        }
        else {
            fields.splice(targetFieldIndex, 0, fieldToMove);
        }
        return { ...typeDraft, fields };
    }
}
class SetActiveSelectorAction {
    selector;
    increaseMenuScrollSignal;
    increaseEditorScrollSignal;
    constructor(selector, increaseMenuScrollSignal, increaseEditorScrollSignal) {
        this.selector = selector;
        this.increaseMenuScrollSignal = increaseMenuScrollSignal;
        this.increaseEditorScrollSignal = increaseEditorScrollSignal;
    }
    reduce(state) {
        if (isEqual(state.activeSelector, this.selector)) {
            return state;
        }
        let { activeSelectorMenuScrollSignal, activeSelectorEditorScrollSignal } = state;
        if (this.increaseMenuScrollSignal) {
            activeSelectorMenuScrollSignal += 1;
        }
        if (this.increaseEditorScrollSignal) {
            activeSelectorEditorScrollSignal += 1;
        }
        return {
            ...state,
            activeSelector: this.selector,
            activeSelectorMenuScrollSignal,
            activeSelectorEditorScrollSignal,
        };
    }
}
class SetNextUpdateSchemaSpecificationIsDueToSaveAction {
    schemaWillBeUpdatedDueToSave;
    constructor(schemaWillBeUpdatedDueToSave) {
        this.schemaWillBeUpdatedDueToSave = schemaWillBeUpdatedDueToSave;
    }
    reduce(state) {
        if (state.schemaWillBeUpdatedDueToSave === this.schemaWillBeUpdatedDueToSave)
            return state;
        return { ...state, schemaWillBeUpdatedDueToSave: this.schemaWillBeUpdatedDueToSave };
    }
}
class UpdateSchemaSpecificationAction {
    schema;
    constructor(schema) {
        this.schema = schema;
    }
    reduce(state) {
        const entityTypes = this.schema.spec.entityTypes.map((entityTypeSpec) => ({
            ...this.convertType('entity', entityTypeSpec),
            authKeyPattern: entityTypeSpec.authKeyPattern,
            existingAuthKeyPattern: entityTypeSpec.authKeyPattern,
            nameField: entityTypeSpec.nameField,
            existingNameField: entityTypeSpec.nameField,
            publishable: entityTypeSpec.publishable,
            existingPublishable: entityTypeSpec.publishable,
        }));
        const componentTypes = this.schema.spec.componentTypes.map((componentTypeSpec) => ({
            ...this.convertType('component', componentTypeSpec),
            adminOnly: componentTypeSpec.adminOnly,
            existingAdminOnly: componentTypeSpec.adminOnly,
        }));
        const indexes = this.schema.spec.indexes.map((indexSpec) => ({
            ...indexSpec,
            status: '',
            existingIndexSpec: indexSpec,
        }));
        const patterns = this.schema.spec.patterns.map((patternSpec) => ({
            ...patternSpec,
            status: '',
            existingPatternSpec: patternSpec,
        }));
        if (!state.schemaWillBeUpdatedDueToSave && state.schema)
            return state; //TODO handle update to schema
        return {
            ...state,
            status: '',
            schema: this.schema,
            schemaWillBeUpdatedDueToSave: false,
            entityTypes,
            deletedEntityTypes: [],
            componentTypes,
            deletedComponentTypes: [],
            indexes,
            deletedIndexes: [],
            patterns,
        };
    }
    convertType(kind, typeSpec) {
        return {
            kind,
            name: typeSpec.name,
            existingName: typeSpec.name,
            status: '',
            deletedFields: [],
            fields: typeSpec.fields.map((fieldSpec) => {
                const fieldDraft = {
                    name: fieldSpec.name,
                    status: '',
                    type: fieldSpec.type,
                    list: fieldSpec.list,
                    required: fieldSpec.required,
                    adminOnly: fieldSpec.adminOnly,
                    existingFieldSpec: fieldSpec,
                };
                if (fieldSpec.type === FieldType.String) {
                    fieldDraft.multiline = fieldSpec.multiline;
                    fieldDraft.index = fieldSpec.index;
                    fieldDraft.matchPattern = fieldSpec.matchPattern;
                    fieldDraft.values = fieldSpec.values;
                }
                if (fieldSpec.type === FieldType.RichText) {
                    fieldDraft.richTextNodesWithPlaceholders = getRichTextNodesWithPlaceholders(fieldSpec.richTextNodes);
                    fieldDraft.existingRichTextNodesWithPlaceholders = [
                        ...fieldDraft.richTextNodesWithPlaceholders,
                    ];
                }
                if (fieldSpec.type === FieldType.Reference || fieldSpec.type === FieldType.RichText) {
                    fieldDraft.entityTypes = fieldSpec.entityTypes;
                }
                if (fieldSpec.type === FieldType.Number) {
                    fieldDraft.integer = fieldSpec.integer;
                }
                if (fieldSpec.type === FieldType.RichText) {
                    fieldDraft.linkEntityTypes = fieldSpec.linkEntityTypes;
                }
                if (fieldSpec.type === FieldType.Component || fieldSpec.type === FieldType.RichText) {
                    fieldDraft.componentTypes = fieldSpec.componentTypes;
                }
                return fieldDraft;
            }),
            existingFieldOrder: typeSpec.fields.map((fieldSpec) => fieldSpec.name),
        };
    }
}
function getRichTextNodesWithPlaceholders(richTextNodes) {
    let result = richTextNodes;
    if (result.length > 0) {
        const placeholders = [];
        result = result.filter((richTextNode) => {
            if (RichTextNodesInPlaceholders.has(richTextNode)) {
                const placeholder = RichTextNodePlaceholders.find((it) => it.nodes.includes(richTextNode));
                assertIsDefined(placeholder);
                if (!placeholders.includes(placeholder.name)) {
                    placeholders.push(placeholder.name);
                }
                return false;
            }
            return true;
        });
        result = [...placeholders, ...result];
        sortRichTextNodesWithPlaceholders(result);
    }
    return result;
}
function getRichTextNodesWithoutPlaceholders(richTextNodesWithPlaceholders) {
    if (!richTextNodesWithPlaceholders || richTextNodesWithPlaceholders.length === 0) {
        return richTextNodesWithPlaceholders;
    }
    const richTextNodes = richTextNodesWithPlaceholders.flatMap((node) => {
        const placeholder = RichTextNodePlaceholders.find((placeholder) => placeholder.name === node);
        if (placeholder) {
            return placeholder.nodes;
        }
        return node;
    });
    richTextNodes.sort();
    return richTextNodes;
}
export function sortRichTextNodesWithPlaceholders(richTextNodesWithPlaceholders) {
    if (richTextNodesWithPlaceholders.length <= 1) {
        return;
    }
    richTextNodesWithPlaceholders.sort((a, b) => {
        if (a === REQUIRED_NODES_PLACEHOLDER.name) {
            return -1;
        }
        if (b === REQUIRED_NODES_PLACEHOLDER.name) {
            return 1;
        }
        return a.localeCompare(b);
    });
}
export const SchemaEditorActions = {
    AddField: AddFieldAction,
    AddIndex: AddIndexAction,
    AddPattern: AddPatternAction,
    AddType: AddTypeAction,
    ChangeFieldAdminOnly: ChangeFieldAdminOnlyAction,
    ChangeFieldAllowedComponentTypes: ChangeFieldAllowedComponentTypesAction,
    ChangeFieldAllowedEntityTypes: ChangeFieldAllowedEntityTypesAction,
    ChangeFieldAllowedLinkEntityTypes: ChangeFieldAllowedLinkEntityTypesAction,
    ChangeFieldAllowedRichTextNodes: ChangeFieldAllowedRichTextNodesAction,
    ChangeFieldIndex: ChangeFieldIndexAction,
    ChangeFieldInteger: ChangeFieldIntegerAction,
    ChangeFieldMatchPattern: ChangeFieldMatchPatternAction,
    ChangeFieldMultiline: ChangeFieldMultilineAction,
    ChangeFieldRequired: ChangeFieldRequiredAction,
    ChangeFieldType: ChangeFieldTypeAction,
    ChangeFieldValues: ChangeFieldValuesAction,
    ChangePatternPattern: ChangePatternPatternAction,
    ChangeTypeAdminOnly: ChangeTypeAdminOnlyOrPublishableAction,
    ChangeTypeAuthKeyPattern: ChangeTypeAuthKeyPatternAction,
    ChangeTypeNameField: ChangeTypeNameFieldAction,
    DeleteField: DeleteFieldAction,
    DeleteIndex: DeleteIndexAction,
    DeletePattern: DeletePatternAction,
    DeleteType: DeleteTypeAction,
    RenameField: RenameFieldAction,
    RenameIndex: RenameIndexAction,
    RenamePattern: RenamePatternAction,
    RenameType: RenameTypeAction,
    ReorderFields: ReorderFieldsAction,
    SetActiveSelector: SetActiveSelectorAction,
    SetNextUpdateSchemaSpecificationIsDueToSave: SetNextUpdateSchemaSpecificationIsDueToSaveAction,
    UpdateSchemaSpecification: UpdateSchemaSpecificationAction,
};
// CONVERSION
export function getSchemaSpecificationUpdateFromEditorState(state) {
    const update = {};
    const entityTypes = state.entityTypes
        .filter((it) => it.status !== '')
        .map(getTypeUpdateFromEditorState);
    const componentTypes = state.componentTypes
        .filter((it) => it.status !== '')
        .map(getTypeUpdateFromEditorState);
    const indexes = state.indexes
        .filter((it) => it.status !== '')
        .map(({ name, type }) => ({ name, type }));
    const patterns = state.patterns
        .filter((it) => it.status !== '')
        .map(({ name, pattern }) => ({ name, pattern }));
    const migrations = getMigrationsFromEditorState(state);
    const transientMigrations = getTransientMigrationsFromEditorState(state);
    if (entityTypes.length > 0) {
        update.entityTypes = entityTypes;
    }
    if (componentTypes.length > 0) {
        update.componentTypes = componentTypes;
    }
    if (indexes.length > 0) {
        update.indexes = indexes;
    }
    if (patterns.length > 0) {
        update.patterns = patterns;
    }
    if (migrations.length > 0) {
        update.migrations = migrations;
    }
    if (transientMigrations.length > 0) {
        update.transientMigrations = transientMigrations;
    }
    if (Object.keys(update).length > 0 && state.schema) {
        update.version = state.schema.spec.version + 1;
    }
    return update;
}
function getTypeUpdateFromEditorState(draftType) {
    const fields = draftType.fields.map((draftField) => {
        return {
            name: draftField.name,
            type: draftField.type,
            required: draftField.required,
            adminOnly: draftField.adminOnly,
            ...(draftField.list ? { list: draftField.list } : undefined),
            ...(draftField.type === FieldType.String
                ? {
                    multiline: draftField.multiline,
                    index: draftField.index ?? null,
                    matchPattern: draftField.matchPattern ?? null,
                    values: draftField.values ?? [],
                }
                : undefined),
            ...(draftField.type === FieldType.RichText
                ? {
                    richTextNodes: getRichTextNodesWithoutPlaceholders(draftField.richTextNodesWithPlaceholders),
                    linkEntityTypes: draftField.linkEntityTypes ?? [],
                }
                : undefined),
            ...(draftField.type === FieldType.Number ? { integer: !!draftField.integer } : undefined),
            ...(draftField.type === FieldType.Reference || draftField.type === FieldType.RichText
                ? { entityTypes: draftField.entityTypes ?? [] }
                : undefined),
            ...(draftField.type === FieldType.Component || draftField.type === FieldType.RichText
                ? { componentTypes: draftField.componentTypes ?? [] }
                : undefined),
        };
    });
    const shared = { name: draftType.name, fields };
    if (draftType.kind === 'entity') {
        return {
            ...shared,
            authKeyPattern: draftType.authKeyPattern,
            nameField: draftType.nameField,
            publishable: draftType.publishable,
        };
    }
    else {
        return { ...shared, adminOnly: draftType.adminOnly };
    }
}
function getMigrationsFromEditorState(state) {
    const actions = [];
    for (const typeName of state.deletedEntityTypes) {
        actions.push({ action: 'deleteType', entityType: typeName });
    }
    for (const typeName of state.deletedComponentTypes) {
        actions.push({ action: 'deleteType', componentType: typeName });
    }
    for (const typeDraft of [...state.entityTypes, ...state.componentTypes]) {
        if (typeDraft.status !== 'changed') {
            continue;
        }
        const typeName = typeDraft.kind === 'entity'
            ? { entityType: typeDraft.name }
            : { componentType: typeDraft.name };
        if (typeDraft.existingName && typeDraft.existingName !== typeDraft.name) {
            actions.push({
                action: 'renameType',
                ...(typeDraft.kind === 'entity'
                    ? { entityType: typeDraft.existingName }
                    : { componentType: typeDraft.existingName }),
                newName: typeDraft.name,
            });
        }
        for (const fieldName of typeDraft.deletedFields) {
            actions.push({ action: 'deleteField', ...typeName, field: fieldName });
        }
        for (const fieldDraft of typeDraft.fields) {
            if (fieldDraft.existingFieldSpec && fieldDraft.existingFieldSpec.name !== fieldDraft.name) {
                actions.push({
                    action: 'renameField',
                    ...typeName,
                    field: fieldDraft.existingFieldSpec.name,
                    newName: fieldDraft.name,
                });
            }
        }
    }
    if (actions.length > 0) {
        return [{ version: (state.schema?.spec.version ?? 0) + 1, actions }];
    }
    return [];
}
function getTransientMigrationsFromEditorState(state) {
    const actions = [];
    for (const index of state.deletedIndexes) {
        actions.push({ action: 'deleteIndex', index });
    }
    for (const indexDraft of state.indexes) {
        const existingName = indexDraft.existingIndexSpec?.name;
        if (existingName && existingName !== indexDraft.name) {
            actions.push({ action: 'renameIndex', index: existingName, newName: indexDraft.name });
        }
    }
    return actions;
}
// SELECTORS
export function getElementIdForSelector(selector, section) {
    if (!selector) {
        return undefined;
    }
    if (selector.kind === 'index') {
        return `index-${selector.name}-${section}`;
    }
    if (selector.kind === 'pattern') {
        return `pattern-${selector.name}-${section}`;
    }
    return `${selector.typeName}-${section}`;
}
//# sourceMappingURL=SchemaEditorReducer.js.map